import { useCallback, useEffect, useState } from 'react';

import { AlertProps } from '../../components/UI/Alert/types';
import { usePets } from '../../context/PetsContext';
import {
  Address as AddressType,
  Address_Defaults,
  SolicitudCambioUserReq,
  User,
  UserReq,
} from '../../types/User/types';
import { normalizeEmptyToNull } from '../../utils/form';
import { hasChangesInUserBody } from '../../utils/user';
import { useAlert } from '../UI/useAlert';
import { useStepNavigation } from '../UI/useStepNavigation';
import {
  useGetLocalidades,
  useGetProvincia,
  usePostUser,
  useSolicitudCambioUser,
} from './useUser';

// Tipo para las localidades (ajusta según tu useGetLocalidades)
interface LocalidadOption {
  value: string;
  label: string;
}

interface UseUserFormProps {
  userData?: User;
  addressData?: AddressType;
  closeModal?: () => void;
  isRequired?: boolean;
  submitMode?: 'auto' | 'direct';
  totalSteps: number;
}

interface UseUserFormReturn {
  // Estado
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  address: AddressType | null;
  setAddress: React.Dispatch<React.SetStateAction<AddressType | null>>;
  isInitialized: boolean;
  isSubmissionComplete: boolean;

  // Navegación
  currentStep: number;
  totalSteps: number;
  isFirst: boolean;
  next: () => void;
  back: () => void;

  // Provincias y localidades
  localidades: LocalidadOption[];
  isFetchingLocalidades: boolean;
  handleProvinciaChange: (newProvincia: string) => void;

  // Submit
  onSubmit: (addressFromForm?: AddressType) => Promise<boolean>;
  isSubmitting: boolean;

  // Alertas
  alertState: AlertProps;
  hideAlert: () => void;
}

export const useUserForm = ({
  userData,
  addressData,
  closeModal,
  isRequired = false,
  submitMode = 'auto',
  totalSteps,
}: UseUserFormProps): UseUserFormReturn => {
  // API Hooks
  const { currentPet } = usePets();
  const { postUser, isLoading: submittingNewUser } = usePostUser();
  const { postSolicitudCambioUser, isLoading: submittingSolicitudCambioUser } =
    useSolicitudCambioUser();

  // UI Hooks
  const { alertState, showAlert, hideAlert } = useAlert();
  const {
    currentStep,
    totalSteps: steps,
    isFirst,
    next,
    back,
  } = useStepNavigation(totalSteps);

  // Estado local
  const [user, setUser] = useState<User | null>(userData ?? null);
  const [address, setAddress] = useState<AddressType | null>(
    addressData ?? Address_Defaults,
  );
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isSubmissionComplete, setIsSubmissionComplete] = useState<boolean>(false);

  // Obtener provincia basada en localidad
  const shouldFetchProvincia = Boolean(address?.localidad && !address?.provincia);
  const { provincia: fetchedProvincia, isValidating: isFetchingProvincia } =
    useGetProvincia(shouldFetchProvincia ? address?.localidad : null);

  // Obtener localidades basado en la provincia seleccionada
  const selectedProvincia = address?.provincia;
  const { localidades, isValidating: isFetchingLocalidades } =
    useGetLocalidades(selectedProvincia);

  // Handlers
  const handleProvinciaChange = useCallback((newProvincia: string) => {
    setAddress((prev) => {
      if (!prev) return prev;
      if (prev.provincia !== newProvincia) {
        return { ...prev, provincia: newProvincia, localidad: '' };
      }
      return prev;
    });
  }, []);

  // Submit handler
  const onSubmit = useCallback(
    async (addressFromForm?: AddressType): Promise<boolean> => {
      const addressOk = addressFromForm ?? address;
      if (!user || !addressOk) return false;

      const normalizedAddress = {
        ...addressOk,
        localidad: Number(addressOk.localidad),
      };

      try {
        const shouldCreateUser = !userData || submitMode === 'direct';

        if (shouldCreateUser) {
          const body: UserReq = {
            datos_persona: user,
            datos_domicilio: normalizedAddress,
          };

          const cleaned = normalizeEmptyToNull(body);
          const res = await postUser(cleaned);

          if (!res) {
            showAlert({
              header: 'Error',
              message:
                'No pudimos guardar tus datos. Revisá la información e intentá nuevamente.',
              buttons: [{ text: 'Entendido' }],
            });
            return false;
          }

          setIsSubmissionComplete(true);
          showAlert({
            header: '¡Éxito!',
            message:
              submitMode === 'direct'
                ? 'Recibimos tus datos correctamente. Ya podés continuar.'
                : 'Guardamos tus datos correctamente.',
            buttons: [{ text: 'Entendido' }],
            onDidDismiss: closeModal,
          });

          return true;
        }

        // EDICIÓN de usuario
        if (!currentPet?.id) {
          showAlert({
            header: 'Mensaje importante',
            message:
              'Debes tener al menos una mascota para editar tus datos personales. Por favor, agrega una mascota e intentá nuevamente.',
            buttons: [{ text: 'Entendido' }],
            onDidDismiss: closeModal,
          });
          return false;
        }

        const body: SolicitudCambioUserReq = {
          mascotaId: currentPet.id,
          nombre: user.nombre,
          apellido: user.apellido,
          dni: user.dni,
          fechaNac:
            user.fecha_nacimiento instanceof Date
              ? user.fecha_nacimiento.toISOString()
              : user.fecha_nacimiento,
          telCelular: user.tel_celular,
          genero: user.genero,
          direccion: normalizedAddress.direccion,
          numero: normalizedAddress.numero,
          piso: normalizedAddress.piso ?? null,
          dpto: normalizedAddress.departamento ?? null,
          provincia: normalizedAddress.provincia,
          localidad: normalizedAddress.localidad,
        };

        if (!hasChangesInUserBody(userData, addressData, body)) {
          showAlert({
            header: 'Sin cambios',
            message:
              'No se detectaron modificaciones en tus datos. Realizá al menos un cambio para enviar la solicitud.',
            buttons: [{ text: 'Entendido' }],
          });
          return false;
        }

        const res = await postSolicitudCambioUser(body);

        if (!res) {
          showAlert({
            header: 'Error',
            message:
              'No pudimos registrar la solicitud de cambio de tus datos personales. Revisá la información e intentá nuevamente.',
            buttons: [{ text: 'Entendido' }],
          });
          return false;
        }

        setIsSubmissionComplete(true);
        showAlert({
          header: '¡Éxito!',
          message:
            'Se envio una solicitud de cambio de tus datos personales. Los mismos serán revisados por nuestro equipo y se aplicarán en los próximos días.',
          buttons: [{ text: 'Entendido' }],
          onDidDismiss: closeModal,
        });

        return true;
      } catch (error) {
        console.error('Error al guardar usuario:', error);

        showAlert({
          header: 'Error',
          message: userData
            ? 'Ocurrió un problema al editar sus datos. Intente nuevamente más tarde.'
            : 'Ocurrió un problema al registrar sus datos. Intente nuevamente más tarde.',
          buttons: [{ text: 'Entendido' }],
          onDidDismiss: isRequired ? undefined : closeModal,
        });

        return false;
      }
    },
    [
      currentPet,
      user,
      address,
      userData,
      addressData,
      postUser,
      postSolicitudCambioUser,
      showAlert,
      closeModal,
      isRequired,
      submitMode,
    ],
  );

  // Sincronizar provincia cuando se carga por primera vez
  useEffect(() => {
    if (!fetchedProvincia || !address || address.provincia) return;

    setAddress((prev) => {
      if (!prev || prev.provincia) return prev;
      return { ...prev, provincia: fetchedProvincia };
    });
  }, [fetchedProvincia, address]);

  // Inicialización
  useEffect(() => {
    if (!userData) {
      setIsInitialized(true);
      return;
    }

    if (user && address && !isFetchingProvincia) {
      setIsInitialized(true);
    }
  }, [userData, user, address, isFetchingProvincia]);

  return {
    // Estado
    user,
    setUser,
    address,
    setAddress,
    isInitialized,
    isSubmissionComplete,

    // Submit
    onSubmit,
    isSubmitting: submittingNewUser || submittingSolicitudCambioUser,

    // Provincias y localidades
    localidades,
    isFetchingLocalidades,
    handleProvinciaChange,

    // Navegación
    currentStep,
    totalSteps: steps,
    isFirst,
    next,
    back,

    // Alertas
    alertState,
    hideAlert,
  };
};
